// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * Pre-snapshot phase narration for the cycle dashboard (issue
 * `gen0-milestone-narration`, US §3/§4; PRD §Solution part 2). Already
 * -fetched data in (the relevant event names + whether a snapshot
 * exists), a display string out — no fetch here (INV-3).
 *
 * Fills the long generation-0 dead zone: while population-initialization
 * has started but not completed and no snapshot exists yet, the
 * dashboard says "Initializing population…" so a silent init is
 * distinguishable from a hung cycle. The completed milestone (or the
 * first snapshot) flips the narration off — evolution proper has
 * started, and the live panels take over.
 */

import type { CycleProgress } from "../_progressPayload"

const POP_INIT_STARTED = "PopulationInitializationStartedEvent"
const POP_INIT_COMPLETED = "PopulationInitializationCompletedEvent"

export const INITIALIZING_POPULATION = "Initializing population…"

export const selectInitPhaseNarration = (
  eventNames: readonly string[],
  hasSnapshot: boolean,
): string | null => {
  if (hasSnapshot) return null
  const started = eventNames.includes(POP_INIT_STARTED)
  const completed = eventNames.includes(POP_INIT_COMPLETED)
  return started && !completed ? INITIALIZING_POPULATION : null
}

/**
 * The live phase label carried by the accumulating tier (US §9). Once a
 * progress row exists it supersedes the milestone-derived narration; an
 * absent progress row or a blank phase leaves the milestone path as the
 * pre-progress fallback.
 */
export const selectPhaseLabel = (
  progress: CycleProgress | null,
): string | null => {
  const phase = progress?.current_phase?.trim()
  return phase ? phase : null
}
